import { Fragment, useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";
import { Users, LayoutDashboard, HelpCircle, X } from "lucide-react";
import { T, FAQ } from "../lib/i18n";
import { friendlyError, joinSoftWraps } from "../lib/helpers";
import { splitFormatting } from "../lib/formatting";

// Dialogsemantik för modaler: role=dialog, Escape stänger, fokus flyttas in,
// hålls kvar (Tab-fälla) och återställs till utlösande element vid stängning
function useDialog(onClose) {
  const ref = useRef(null);
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; });
  useEffect(() => {
    const prev = document.activeElement;
    if (ref.current) ref.current.focus({ preventScroll: true });
    function onKey(e) {
      if (e.key === "Escape") {
        e.stopPropagation();
        if (closeRef.current) closeRef.current();
        return;
      }
      if (e.key === "Tab" && ref.current) {
        const els = ref.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (els.length === 0) return;
        const first = els[0], last = els[els.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      if (prev && prev.focus) prev.focus({ preventScroll: true });
    };
  }, []);
  return ref;
}

function Dialog({ onClose, label, style, children, ...rest }) {
  const ref = useDialog(onClose);
  return (
    <div ref={ref} role="dialog" aria-modal="true" aria-label={label} tabIndex={-1}
      onClick={e => e.stopPropagation()} style={{ outline: "none", ...style }} {...rest}>
      {children}
    </div>
  );
}

// Bekräftelsedialog i appens egen design — ersätter native window.confirm(),
// som bröt mot resten av UI:t och inte kunde formuleras om per språk.
// Ärver Dialog:s tangentbordsstöd (Escape, fokusfälla, fokusåterställning).
function ConfirmDialog({ text, confirmLabel, cancelLabel, onConfirm, onCancel, danger = true, lang = "sv", G, css }) {
  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <Dialog onClose={onCancel} label={confirmLabel || (lang === "sv" ? "Bekräfta" : "Confirm")}
        style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 16, padding: "24px 26px", maxWidth: 420, width: "100%", boxShadow: "0 20px 50px rgba(0,0,0,0.3)" }}>
        <div style={{ fontSize: 15, color: G.text, lineHeight: 1.6, marginBottom: 20 }}>{text}</div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onCancel}
            style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 10, color: G.text, fontSize: 14, padding: "9px 16px", cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
            {cancelLabel || (lang === "sv" ? "Avbryt" : "Cancel")}
          </button>
          <button onClick={onConfirm} autoFocus
            style={{ background: danger ? G.red : G.gold, border: "none", borderRadius: 10, color: danger ? "#fff" : G.onGold, fontSize: 14, fontWeight: 600, padding: "9px 18px", cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
            {confirmLabel || (lang === "sv" ? "Ta bort" : "Delete")}
          </button>
        </div>
      </Dialog>
    </div>
  );
}

// Figurer som hör till en fråga. Accepterar både publika URL:er (sparad tRAT)
// och lokala blob-URL:er (förhandsvisning direkt efter PDF-import).
// Klick öppnar bilden i full storlek — figurer i provfrågor är ofta detaljerade.
// Klick på en bild öppnar den i fullskärm inne i appen. Tidigare öppnades
// Supabase-URL:en i en ny flik, vilket på mobil såg ut som att man lämnat appen.
function ImageLightbox({ src, alt, onClose, lang, G }) {
  const closeLabel = lang === "sv" ? "Stäng" : "Close";
  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 10001, background: "rgba(0,0,0,0.92)", display: "flex", alignItems: "center", justifyContent: "center", padding: 12, overflow: "auto", touchAction: "pinch-zoom" }}>
      <Dialog onClose={onClose} label={alt} style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
        <img src={src} alt={alt} onClick={onClose}
          style={{ display: "block", maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 6 }} />
        <button onClick={onClose} aria-label={closeLabel}
          style={{ position: "fixed", top: "max(12px, env(safe-area-inset-top))", right: 12, background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 999, color: "#fff", width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
          <X size={20} />
        </button>
      </Dialog>
    </div>
  );
}

// Lärarens text med fetstil och kursiv (se lib/formatting), utan styckeindelning:
// för korta rader, avkortade listor och återkopplingen bakom 💡. Formateringen
// får aldrig kunna fälla studentvyn, så blir något fel visas texten som den står.
function RichText({ text }) {
  let parts;
  try { parts = splitFormatting(text); } catch { return String(text ?? ""); }
  if (parts.length === 1 && !parts[0].bold && !parts[0].italic) return parts[0].text;
  return parts.map((p, i) => {
    const inner = p.italic ? <em>{p.text}</em> : p.text;
    return p.bold ? <strong key={i}>{inner}</strong> : <Fragment key={i}>{inner}</Fragment>;
  });
}

// Frågetext med stycken. Importen lägger "\n" mellan styckena ("Begrepp: …",
// fallbeskrivningen, själva frågan), och läraren kan göra detsamma i
// redigeraren. En vanlig div slog ihop allt till en enda klump.
function QuestionText({ text, style }) {
  // Rader som fortsätter med liten bokstav är radbrytningar ur en PDF, inte stycken
  const paras = joinSoftWraps(text).split(/\n+/).map(p => p.trim()).filter(Boolean);
  if (paras.length === 0) return null;
  return (
    <div style={style}>
      {paras.map((p, i) => (
        <p key={i} style={{ margin: i < paras.length - 1 ? "0 0 0.6em" : 0 }}><RichText text={p} /></p>
      ))}
    </div>
  );
}

function QuestionImages({ images, G, lang = "sv", maxHeight = 420 }) {
  const [open, setOpen] = useState(null); // index på bilden som visas i fullskärm
  if (!images || images.length === 0) return null;
  const srcs = images.map(im => (typeof im === "string" ? im : im?.previewUrl)).filter(Boolean);
  if (srcs.length === 0) return null;
  const alt = lang === "sv" ? "Figur till frågan" : "Figure for the question";
  const altFor = i => (srcs.length > 1 ? `${alt} ${i + 1}` : alt);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, margin: "10px 0" }}>
      {srcs.map((src, i) => (
        <button key={i} type="button" onClick={() => setOpen(i)}
          title={lang === "sv" ? "Visa i fullskärm" : "View full screen"}
          aria-label={lang === "sv" ? `Visa ${altFor(i).toLowerCase()} i fullskärm` : `View ${altFor(i).toLowerCase()} full screen`}
          style={{ display: "block", width: "100%", padding: 0, border: `1px solid ${G.border}`, borderRadius: 10, overflow: "hidden", background: G.surface2, cursor: "zoom-in" }}>
          <img src={src} alt={altFor(i)}
            style={{ display: "block", width: "100%", height: "auto", maxHeight, objectFit: "contain" }} />
        </button>
      ))}
      {open !== null && srcs[open] && (
        <ImageLightbox src={srcs[open]} alt={altFor(open)} onClose={() => setOpen(null)} lang={lang} G={G} />
      )}
    </div>
  );
}

// Renders text with tRAT/tRATs exempt from textTransform: uppercase
function LabelText({ text }) {
  const parts = text.split(/(tRATs?)/g);
  if (parts.length === 1) return text;
  return (
    <>{parts.map((part, i) =>
      /^tRATs?$/.test(part)
        ? <span key={i} style={{ textTransform: "none" }}>{part}</span>
        : part
    )}</>
  );
}

function QRDisplay({ text, G, size = 160 }) {
  // Generated locally — session codes are quasi-credentials and were
  // previously sent to a third-party API (api.qrserver.com)
  const [dataUrl, setDataUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    import("qrcode").then(QRCode =>
      QRCode.toDataURL(text, {
        width: size * 2, margin: 1,
        color: { dark: G.gold, light: G.surface },
      })
    ).then(url => { if (!cancelled) setDataUrl(url); })
     .catch(e => console.error("QR:", e));
    return () => { cancelled = true; };
  }, [text, size, G.gold, G.surface]);
  return (
    <div style={{ textAlign: "center", padding: "12px 0" }}>
      {dataUrl
        ? <img src={dataUrl} alt={`QR-kod: ${text}`} width={size} height={size} style={{ borderRadius: 12, border: `1px solid ${G.border}`, display: "block", margin: "0 auto" }} />
        : <div style={{ width: size, height: size, margin: "0 auto", borderRadius: 12, border: `1px solid ${G.border}`, background: G.surface2 }} />}
      <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 12, color: G.muted, marginTop: 8 }}>{text}</div>
    </div>
  );
}

// ─────────────────────────────────────────
//  HELP MODAL
// ─────────────────────────────────────────
// audience: "teacher" visar de tre lärarflikarna, "student" en kort
// studentvy (så går det till + de vanliga frågor som rör studenter).
function HelpModal({ show, onClose, initialTab = 'what', audience = 'teacher', lang, G, css }) {
  const [tab, setTab] = useState(initialTab);
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const t = T[lang];
  const isStudent = audience === 'student';
  const faqs = isStudent ? FAQ[lang].filter(f => f.student) : FAQ[lang];
  const tabs = isStudent
    ? [{ id: 'student', label: t.howItWorks }, { id: 'faq', label: t.helpModalFAQ }]
    : [{ id: 'what', label: t.helpModalWhat }, { id: 'how', label: t.helpModalHow }, { id: 'faq', label: t.helpModalFAQ }];
  
  useEffect(() => {
    setTab(initialTab);
  }, [initialTab, show]);
  
  if (!show) return null;
  
  return (
    <div 
      style={{ 
        position: "fixed", 
        inset: 0, 
        background: "rgba(0,0,0,0.6)", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center", 
        padding: 20,
        zIndex: 10000
      }}
      onClick={onClose}
    >
      <Dialog
        onClose={onClose}
        label={lang === "sv" ? "Hjälp" : "Help"}
        style={{
          background: G.surface,
          border: `1px solid ${G.border}`,
          borderRadius: 18,
          padding: "32px",
          maxWidth: 700,
          width: "100%",
          position: "relative",
          maxHeight: "85vh",
          overflowY: "auto"
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: 16,
            right: 16,
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 8,
            display: "flex",
            color: G.muted,
            transition: "color 0.2s"
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = G.text}
          onMouseLeave={(e) => e.currentTarget.style.color = G.muted}
        >
          <X size={20} />
        </button>
        
        {/* Tabs */}
        <div style={{ 
          display: "flex", 
          gap: 0, 
          marginBottom: 24, 
          borderBottom: `2px solid ${G.border}` 
        }}>
          {tabs.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => {
                setTab(id);
                setExpandedFAQ(null);
              }}
              style={{
                background: "transparent",
                color: tab === id ? G.text : G.muted,
                border: "none",
                borderBottom: tab === id ? `3px solid ${G.gold}` : "3px solid transparent",
                padding: "10px 20px",
                fontSize: 14,
                fontWeight: tab === id ? 700 : 500,
                cursor: "pointer",
                marginBottom: -2,
                transition: "all 0.2s"
              }}
              onMouseEnter={(e) => {
                if (tab !== id) {
                  e.target.style.color = G.text;
                  e.target.style.background = G.surface2;
                }
              }}
              onMouseLeave={(e) => {
                if (tab !== id) {
                  e.target.style.color = G.muted;
                  e.target.style.background = "transparent";
                }
              }}
            >
              {label}
            </button>
          ))}
        </div>
        
        {/* Content */}
        <div style={{ color: G.text, fontSize: 14, lineHeight: 1.7 }}>
          {tab === 'what' && (
            <div style={{ whiteSpace: "pre-line" }}>
              {t.whatIsTRATText}
            </div>
          )}

          {tab === 'student' && (
            <div>
              <p style={{ margin: "0 0 16px", color: G.sub || G.muted }}>{t.helpStudentIntro}</p>
              <ol style={{ margin: 0, paddingLeft: 24, lineHeight: 1.7, display: "flex", flexDirection: "column", gap: 10 }}>
                {t.helpStudentSteps.map((step, i) => <li key={i}>{step}</li>)}
              </ol>
            </div>
          )}
          
          {tab === 'how' && (
            <div>
              <h3 style={{ ...css.heading, fontSize: 18, marginBottom: 16, color: G.gold, display: "flex", alignItems: "center", gap: 8 }}>
                <LayoutDashboard size={20} />
                {t.howItWorksTeacher}
              </h3>
              <ol style={{ marginBottom: 32, paddingLeft: 24, lineHeight: 1.8 }}>
                <li>{t.howTeacherStep1}</li>
                <li>
                  {t.howTeacherStep2}
                  <br/>
                  <span style={{ color: G.muted, fontSize: 14 }}>
                    → {t.howTeacherStep2Sub}
                  </span>
                </li>
                <li>{t.howTeacherStep3}</li>
                <li>{t.howTeacherStep4}</li>
              </ol>
              
              <h3 style={{ ...css.heading, fontSize: 18, marginBottom: 16, color: G.accent, display: "flex", alignItems: "center", gap: 8 }}>
                <Users size={20} />
                {t.howItWorksStudent}
              </h3>
              <ol style={{ paddingLeft: 24, lineHeight: 1.8 }}>
                <li>{t.howStudentStep1}</li>
                <li>{t.howStudentStep2}</li>
                <li>{t.howStudentStep3}</li>
                <li>
                  {t.howStudentStep4}
                  <br/>
                  <span style={{ color: G.muted, fontSize: 14 }}>
                    {t.howStudentStep4Sub}
                  </span>
                </li>
              </ol>
            </div>
          )}
          
          {tab === 'faq' && (
            <div>
              {faqs.map((faq, i) => (
                <div 
                  key={i} 
                  style={{ 
                    marginBottom: 12, 
                    borderBottom: i < faqs.length - 1 ? `1px solid ${G.border}` : 'none', 
                    paddingBottom: 12 
                  }}
                >
                  <button
                    onClick={() => setExpandedFAQ(expandedFAQ === i ? null : i)}
                    style={{
                      background: "none",
                      border: "none",
                      width: "100%",
                      textAlign: "left",
                      padding: "8px 0",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      color: G.text,
                      fontWeight: 600,
                      fontSize: 14,
                      transition: "color 0.2s"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = G.gold}
                    onMouseLeave={(e) => e.currentTarget.style.color = G.text}
                  >
                    <span style={{ flexShrink: 0, transition: "transform 0.2s", transform: expandedFAQ === i ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                      ▶
                    </span>
                    <span style={{ flex: 1 }}>{faq.q}</span>
                  </button>
                  {expandedFAQ === i && (
                    <div style={{ 
                      paddingLeft: 28, 
                      paddingTop: 8, 
                      color: G.text,
                      opacity: 0.9,
                      fontSize: 14,
                      lineHeight: 1.7,
                      animation: "fadeIn 0.2s ease-in"
                    }}>
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────
//  APP HEADER
// ─────────────────────────────────────────
function HoverTip({ label, children, G }) {
  const [show, setShow] = useState(false);
  return (
    <div
      style={{ position: "relative", display: "inline-flex" }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onFocus={() => setShow(true)}
      onBlur={() => setShow(false)}
    >
      {children}
      {show && (
        <div
          role="tooltip"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            background: G.text,
            color: G.bg,
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 11,
            fontWeight: 500,
            padding: "4px 8px",
            borderRadius: 5,
            whiteSpace: "nowrap",
            pointerEvents: "none",
            zIndex: 100,
            opacity: 0.95,
          }}
        >
          {label}
        </div>
      )}
    </div>
  );
}

function AppHeader({ lang, setLang, onHome, subtitle, theme, setTheme, onHelp, children, G, css }) {
  const t = T[lang];
  return (
    <div style={{ background: G.surface, borderBottom: `1px solid ${G.border}`, padding: "13px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, position: "sticky", top: 0, zIndex: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <button onClick={onHome} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 8, color: G.gold, fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 13, letterSpacing: "0.1em", padding: "5px 12px", cursor: "pointer", transition: "opacity 0.15s" }} onMouseEnter={e => e.currentTarget.style.opacity = "0.7"} onMouseLeave={e => e.currentTarget.style.opacity = "1"}>tRAT</button>
        {subtitle && <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 13, color: G.muted }}>{subtitle}</div>}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {children}
        <HoverTip label={theme === "dark" ? t.lightMode : t.darkMode} G={G}>
          <button onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            aria-label={theme === "dark" ? t.lightMode : t.darkMode}
            style={{ background: G.surface2, border: `1px solid ${G.border}`, borderRadius: 8, color: G.muted, padding: "5px 10px", cursor: "pointer", display: "flex", alignItems: "center" }}>
            {theme === "dark"
              ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
              : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
            }
          </button>
        </HoverTip>
        <HoverTip label={lang === "en" ? "Byt till svenska" : "Switch to English"} G={G}>
          <button onClick={() => setLang(lang === "en" ? "sv" : "en")}
            aria-label={lang === "en" ? "Byt till svenska" : "Switch to English"}
            style={{ background: G.surface2, border: `1px solid ${G.border}`, borderRadius: 8, color: G.muted, fontSize: 12, padding: "4px 9px", cursor: "pointer", fontFamily: "'DM Mono',monospace", display: "flex", alignItems: "center", gap: 4 }}>
            {lang === "en" ? <>🇸🇪 <span>SV</span></> : <>🇬🇧 <span>EN</span></>}
          </button>
        </HoverTip>
        {onHelp && (
          <HoverTip label={lang === "sv" ? "Hjälp" : "Help"} G={G}>
            <button
              onClick={onHelp}
              aria-label={lang === "sv" ? "Hjälp" : "Help"}
              style={{
                background: "none",
                border: `1.5px solid ${G.border}`,
                borderRadius: 8,
                cursor: "pointer",
                padding: "5px 10px",
                color: G.muted,
                display: "flex",
                alignItems: "center",
                transition: "all 0.2s"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = G.gold;
                e.currentTarget.style.color = G.gold;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = G.border;
                e.currentTarget.style.color = G.muted;
              }}
            >
              <HelpCircle size={16} />
            </button>
          </HoverTip>
        )}
      </div>
    </div>
  );
}

function Footer({ G }) {
  return (
    <div style={{ textAlign: "center", padding: "40px 20px 32px", color: G.muted, fontSize: 14, fontFamily: "'DM Sans',sans-serif", borderTop: `1px solid ${G.border}`, marginTop: 60 }}>
      <div style={{ marginBottom: 12 }}>
        © 2026 Team-Based Learning · tRAT
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
        <span>Support:</span>
        <a href="mailto:johan.agardh@med.lu.se" style={{ color: G.gold, textDecoration: "none", fontWeight: 600 }}>
          johan.agardh@med.lu.se
        </a>
        <span style={{ color: G.border }}>•</span>
        <span>Lunds universitet</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
//  LOGIN SCREEN
// ─────────────────────────────────────────
// Shown after an invite or password-reset link — previously those links
// landed on "/" with no way to choose a password (dead-end flow)
// Inbjudnings- och återställningslänkar loggar in användaren direkt. Förut bad
// vi då om ett lösenord "så att du kan logga in med e-post nästa gång" — men
// e-postinloggningen är borttagen ur vyn (2026-09-02), så ett lösenord hade
// blivit ett lösenord utan dörr. Berätta i stället vad som gäller nästa gång.
function WelcomeLUModal({ onClose, lang, G, css }) {
  const sv = lang === "sv";
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 10001, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <Dialog onClose={onClose} label={sv ? "Välkommen" : "Welcome"} style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 18, padding: "32px 28px", maxWidth: 420, width: "100%" }}>
        <h3 style={{ ...css.heading, fontSize: 20, marginBottom: 8 }}>{sv ? "Du är inloggad" : "You are signed in"}</h3>
        <p style={{ color: G.muted, fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
          {sv
            ? "Nästa gång loggar du in med ditt LU-konto — samma e-postadress som inbjudan skickades till. Inget lösenord behövs."
            : "Next time, sign in with your LU account — the same email address the invitation was sent to. No password needed."}
        </p>
        <button style={{ ...css.btnPrimary, width: "100%" }} onClick={onClose}>{sv ? "Fortsätt" : "Continue"}</button>
      </Dialog>
    </div>
  );
}

function SetPasswordModal({ onClose, lang, G, css }) {
  const sv = lang === "sv";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  async function save() {
    if (pw.length < 6) { setErr(sv ? "Lösenordet måste vara minst 6 tecken." : "Password must be at least 6 characters."); return; }
    if (pw !== pw2) { setErr(sv ? "Lösenorden matchar inte." : "Passwords do not match."); return; }
    setSaving(true); setErr("");
    const { error } = await supabase.auth.updateUser({ password: pw });
    setSaving(false);
    if (error) { setErr(friendlyError(error, lang)); return; }
    setDone(true);
    setTimeout(onClose, 1600);
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 10001, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <Dialog onClose={onClose} label={sv ? "Välj ett lösenord" : "Choose a password"} style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 18, padding: "32px 28px", maxWidth: 400, width: "100%" }}>
        <h3 style={{ ...css.heading, fontSize: 20, marginBottom: 8 }}>{sv ? "Välj ett lösenord" : "Choose a password"}</h3>
        <p style={{ color: G.muted, fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
          {sv ? "Sätt ett lösenord så att du kan logga in med e-post nästa gång." : "Set a password so you can sign in with email next time."}
        </p>
        {done ? (
          <div style={{ background: `${G.correct}15`, border: `1px solid ${G.correct}40`, borderRadius: 12, padding: "12px 16px", color: G.correct, fontSize: 14 }}>
            ✓ {sv ? "Lösenordet är sparat." : "Password saved."}
          </div>
        ) : (
          <>
            <label style={css.label}>{sv ? "Nytt lösenord" : "New password"}</label>
            <input type="password" style={{ ...css.input, marginBottom: 12 }} value={pw} onChange={e => setPw(e.target.value)} autoFocus
              placeholder={sv ? "Minst 6 tecken" : "At least 6 characters"} onKeyDown={e => e.key === "Enter" && save()} />
            <label style={css.label}>{sv ? "Upprepa lösenordet" : "Repeat password"}</label>
            <input type="password" style={{ ...css.input, marginBottom: 14 }} value={pw2} onChange={e => setPw2(e.target.value)}
              onKeyDown={e => e.key === "Enter" && save()} />
            {err && <p style={{ color: G.red, fontSize: 14, marginBottom: 10 }}>{err}</p>}
            <button style={{ ...css.btnPrimary, width: "100%" }} onClick={save} disabled={saving}>
              {saving ? (sv ? "Sparar…" : "Saving…") : (sv ? "Spara lösenord" : "Save password")}
            </button>
            <button style={css.btnSecondary} onClick={onClose}>{sv ? "Hoppa över" : "Skip"}</button>
          </>
        )}
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────
//  EDITORIAL COMPONENTS  (iFråga-style, teacher views)
// ─────────────────────────────────────────
const SERIF_FAMILY = "'IBM Plex Serif', Georgia, 'Times New Roman', serif";

// Texten får en minsta bredd att bryta radbrytningen kring (flex-basis), annars
// klämde breda knappar ihop rubrik och undertext till ett ord per rad på mobil.
function PageHead({ title, sub, action, G }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 28, flexWrap: "wrap" }}>
      <div style={{ minWidth: 0, flex: "1 1 260px" }}>
        <h1 style={{ fontFamily: SERIF_FAMILY, fontSize: 28, fontWeight: 400, color: G.text, letterSpacing: "-0.4px", margin: 0, lineHeight: 1.15 }}>{title}</h1>
        {sub && <p style={{ fontSize: 14, color: G.sub, margin: "6px 0 0", lineHeight: 1.45 }}>{sub}</p>}
      </div>
      {action && <div style={{ flexShrink: 0, maxWidth: "100%" }}>{action}</div>}
    </div>
  );
}

function SectionMeta({ children, style = {}, G }) {
  return (
    <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: G.muted, margin: "0 0 12px", fontFamily: "'DM Sans',sans-serif", ...style }}>
      {children}
    </p>
  );
}

function StatGrid({ items, G }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(160px, 1fr))`, gap: 1, marginBottom: 32, border: `1px solid ${G.border}`, borderRadius: 10, overflow: "hidden", background: G.border }}>
      {items.map((s, i) => (
        <div key={i} style={{ background: G.surface, padding: "20px 22px" }}>
          <div style={{ fontSize: 30, fontWeight: 300, color: G.text, letterSpacing: "-1px", fontFamily: SERIF_FAMILY, lineHeight: 1 }}>{s.value}</div>
          <div style={{ fontSize: 12, color: G.sub, marginTop: 6 }}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}

function StatusDot({ color, label, G }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: G.sub }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, flexShrink: 0 }} />
      {label}
    </span>
  );
}

// ─────────────────────────────────────────
//  SERVER TROUBLE BANNER
// ─────────────────────────────────────────
// Visas när servern inte svarat på upprepade försök (useServerTrouble).
// Säger vad som händer och vad man ska göra: vänta, inte ladda om.
function ServerTroubleBanner({ trouble, audience = "student", lang = "sv", G }) {
  if (!trouble) return null;
  const sv = lang === "sv";
  const text = audience === "teacher"
    ? (sv ? "Servern svarar inte just nu. Grupperna kan inte skrapa medan det pågår, men det de redan svarat är sparat. Dashboarden uppdateras av sig själv när servern svarar igen."
          : "The server is not responding right now. Groups cannot scratch meanwhile, but their answers so far are saved. The dashboard updates by itself when the server is back.")
    : (sv ? "Servern svarar inte just nu. Era svar är sparade. Vänta en stund – sidan fortsätter av sig själv, ni behöver inte ladda om."
          : "The server is not responding right now. Your answers are saved. Please wait – the page continues by itself, no need to reload.");
  return (
    <div role="alert" style={{
      position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 9998,
      background: G.gold, color: G.onGold || "#fff",
      padding: "12px 16px calc(12px + env(safe-area-inset-bottom, 0px))",
      fontSize: 14, fontWeight: 600, lineHeight: 1.45, textAlign: "center",
      fontFamily: "'DM Sans', sans-serif", boxShadow: "0 -4px 16px rgba(0,0,0,0.15)",
    }}>
      {text}
    </div>
  );
}

// Visas när en elevflik stått orörd länge och slutat fråga servern (se
// useTabSleep). Första trycket var som helst väcker den.
function SleepBanner({ asleep, lang = "sv", G }) {
  if (!asleep) return null;
  return (
    <div role="status" style={{
      position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 9997,
      background: G.surface2, color: G.text, borderTop: `1px solid ${G.border}`,
      padding: "12px 16px calc(12px + env(safe-area-inset-bottom, 0px))",
      fontSize: 14, fontWeight: 600, lineHeight: 1.45, textAlign: "center",
      fontFamily: "'DM Sans', sans-serif", boxShadow: "0 -4px 16px rgba(0,0,0,0.10)",
    }}>
      {lang === "sv"
        ? "Sidan är pausad eftersom ingen har använt den på ett tag. Tryck var som helst för att fortsätta."
        : "This page is paused because it hasn't been used for a while. Tap anywhere to continue."}
    </div>
  );
}

// En avslutad tRAT är stängd för eleverna: frågorna går inte att hämta (se
// migrationen closed_session_hides_questions) och vyn skickar dem till
// startsidan efter en stund, så att nästa grupp kan börja direkt på en delad
// iPad. score: gruppens poäng om den är känd, t.ex. { total: 31, max: 40 }.
const CLOSED_REDIRECT_SECONDS = 15;
function SessionClosed({ onExit, score, lang = "sv", G, css }) {
  const [left, setLeft] = useState(CLOSED_REDIRECT_SECONDS);
  const exitRef = useRef(onExit);
  exitRef.current = onExit;
  useEffect(() => {
    const i = setInterval(() => setLeft(s => s - 1), 1000);
    return () => clearInterval(i);
  }, []);
  useEffect(() => { if (left <= 0) exitRef.current(); }, [left]);
  const sv = lang === "sv";
  return (
    <div style={{ ...css.page, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div role="status" style={{ ...css.card, textAlign: "center" }}>
        <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 22, fontWeight: 700, color: G.text, marginBottom: 10 }}>
          {sv ? "tRAT:en är avslutad" : "The tRAT has ended"}
        </div>
        <div style={{ fontSize: 15, color: G.sub, lineHeight: 1.6, marginBottom: score ? 12 : 22 }}>
          {sv ? "Läraren har avslutat tRAT:en. Era svar är sparade." : "The teacher has ended the tRAT. Your answers are saved."}
        </div>
        {score && (
          <div style={{ fontSize: 15, color: G.text, fontWeight: 600, marginBottom: 22 }}>
            {sv ? `Ni fick ${score.total} av ${score.max} poäng.` : `You scored ${score.total} of ${score.max}.`}
          </div>
        )}
        <button onClick={() => exitRef.current()} style={{ ...css.btnPrimary, width: "100%" }}>
          {sv ? "Till startsidan" : "Go to the start page"}
        </button>
        <div style={{ fontSize: 13, color: G.muted, marginTop: 12 }}>
          {sv ? `Ni skickas dit automatiskt om ${Math.max(0, left)} s.` : `You will be taken there in ${Math.max(0, left)} s.`}
        </div>
      </div>
    </div>
  );
}

export { SessionClosed, SleepBanner, ServerTroubleBanner, useDialog, Dialog, ConfirmDialog, QuestionImages, QuestionText, RichText, LabelText, QRDisplay, HelpModal, HoverTip, AppHeader, Footer, SetPasswordModal, WelcomeLUModal, SERIF_FAMILY, PageHead, SectionMeta, StatGrid, StatusDot };
